import React from 'react';
import { motion } from 'framer-motion';
import { Contact, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../../store/authStore';
import { APP_VERSION } from '../../../config/version';

interface NavProfileProps {
  isCollapsed?: boolean;
}

const NavProfile: React.FC<NavProfileProps> = ({ isCollapsed = false }) => {
  const { user, isAdmin } = useAuthStore();
  const { t } = useTranslation();

  const getInitials = (name: string | null | undefined) => {
    if (!name) return '';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  if (isCollapsed) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center"
      >
        {user?.photoURL ? (
          <img
            src={user.photoURL}
            alt={user.displayName || 'Profile'}
            className="w-10 h-10 rounded-full ring-2 ring-white dark:ring-white/20"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center ring-2 ring-white dark:ring-white/20">
            <span className="text-blue-600 dark:text-blue-400 text-sm font-medium">
              {getInitials(user?.displayName || user?.email)}
            </span>
          </div>
        )}
        
        {isAdmin && (
          <Crown className="w-4 h-4 text-yellow-500 dark:text-yellow-400 mt-2" />
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/50 dark:to-indigo-900/50 border border-blue-100 dark:border-blue-800 shadow-md"
    >
      <div className="flex items-center space-x-3">
        {user?.photoURL ? (
          <img
            src={user.photoURL}
            alt={user.displayName || 'Profile'}
            className="w-12 h-12 rounded-full ring-2 ring-white dark:ring-white/20 shadow-sm"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center ring-2 ring-white dark:ring-white/20 shadow-sm">
            <span className="text-blue-600 dark:text-blue-400 text-sm font-medium">
              {getInitials(user?.displayName || user?.email)}
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {user?.displayName || user?.email?.split('@')[0]}
          </p>
          
          <div className="flex items-center mt-1">
            {isAdmin ? (
              <>
                <Crown className="w-4 h-4 text-yellow-500 dark:text-yellow-400 mr-1" />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('profile.role.admin')}
                </span>
              </>
            ) : (
              <>
                <Contact className="w-4 h-4 text-blue-500 dark:text-blue-400 mr-1" />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('profile.role.user')}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center space-x-2">
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {APP_VERSION.current}
        </span>
        {APP_VERSION.environment === 'production' ? (
          <span className="px-2 py-0.5 text-[11px] font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full">
            Bêta
          </span>
        ) : (
          <span className="px-2 py-0.5 text-[11px] font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded-full">
            {APP_VERSION.environment}
          </span>
        )}
      </div>
    </motion.div>
  );
};

export default NavProfile;